import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://fiifit.ro';

const api = axios.create({
  baseURL: `${API_URL}/api/tracking/analytics`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Pattern {
  id: string;
  type: 'correlation' | 'anomaly' | 'trend';
  title: string;
  description: string;
  confidence: number;
  metric1: string;
  metric2?: string;
  impact: 'positive' | 'negative' | 'neutral';
  actionable: boolean;
  suggestion?: string;
}

export interface Correlation {
  metric1: string;
  metric2: string;
  coefficient: number;
  strength: 'strong' | 'moderate' | 'weak';
  direction: 'positive' | 'negative';
}

export interface Anomaly {
  date: string;
  metric: string;
  value: number;
  expectedValue: number;
  deviation: number;
  severity: 'low' | 'medium' | 'high';
}

export interface Prediction {
  date: string;
  metric: string;
  predictedValue: number;
  confidence: number;
  reasoning: string;
}

export interface WeeklySummary {
  period: { start: string; end: string };
  sleep: {
    avgDuration: number;
    avgQuality: number;
    trend: 'up' | 'down' | 'stable';
    insights: string[];
  };
  pain: {
    avgLevel: number;
    frequency: number;
    trend: 'up' | 'down' | 'stable';
    commonLocations: string[];
    insights: string[];
  };
  stress: {
    avgLevel: number;
    avgMood: number;
    trend: 'up' | 'down' | 'stable';
    commonTriggers: string[];
    insights: string[];
  };
  vitals: {
    avgGlucose?: number;
    avgBP?: string;
    avgHeartRate?: number;
    alerts: string[];
    insights: string[];
  };
  aiRecommendations: string[];
  topPatterns: Pattern[];
}

export interface AnalyticsSummary {
  patterns: Pattern[];
  correlations: Correlation[];
  anomalies: Anomaly[];
  predictions: Prediction[];
  weeklyReport: WeeklySummary;
}

export const getPatterns = async (days: number = 30): Promise<Pattern[]> => {
  const response = await api.get('/patterns', { params: { days } });
  return response.data.patterns;
};

export const getCorrelations = async (days: number = 30): Promise<Correlation[]> => {
  const response = await api.get('/correlations', { params: { days } });
  return response.data.correlations;
};

export const getAnomalies = async (days: number = 30): Promise<Anomaly[]> => {
  const response = await api.get('/anomalies', { params: { days } });
  return response.data.anomalies;
};

export const getPredictions = async (): Promise<Prediction[]> => {
  const response = await api.get('/predictions');
  return response.data.predictions;
};

export const getWeeklyReport = async (): Promise<WeeklySummary> => {
  const response = await api.get('/weekly-report');
  return response.data.report;
};

export const getAnalyticsSummary = async (forceRefresh: boolean = false): Promise<AnalyticsSummary> => {
  const response = await api.get('/summary', {
    params: { forceRefresh: forceRefresh.toString() }
  });
  return response.data;
};

export const getTrackingDaysCount = async (): Promise<number> => {
  try {
    const response = await api.get('/days-count');
    return response.data.daysCount ?? 0;
  } catch {
    // Fallback: try to infer from summary endpoint or return 0
    return 0;
  }
};
